import { logger } from "../../utils/logger";
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Stack,
  IconButton,
  Autocomplete
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import { companyService } from '../../services/companyService';
import taxService from '../../services/taxService';

const CompanyTaxCode = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [company, setCompany] = useState(null);
  const [taxCodes, setTaxCodes] = useState([]);
  const [selectedTaxCode, setSelectedTaxCode] = useState('');
  const [selectedTaxCodeObject, setSelectedTaxCodeObject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [syncingTaxCodes, setSyncingTaxCodes] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  
  // Check if token exists
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      logger.error('No authentication token found');
      setError('Authentication required. Please log in to access this page.');
      setLoading(false);
      // Redirect to login after a short delay
      setTimeout(() => navigate('/login'), 2000);
    } else {
      setIsAuthenticated(true);
    }
  }, [navigate]);
  
  // Fetch company details
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        if (!isAuthenticated || !companyId) return;
        
        logger.log(`Fetching company details for ID: ${companyId}`);
        setLoading(true);
        setError(null);
        
        const data = await companyService.getCompany(parseInt(companyId));
        logger.log('Company data received:', data);
        
        if (!data) {
          logger.error('Received empty company data');
          setError('Company not found or data is empty');
          setLoading(false);
          return;
        }
        
        setCompany(data);
        if (data.tax_code) {
          setSelectedTaxCode(data.tax_code);
        }
        setLoading(false);
      } catch (err) {
        logger.error('Error fetching company:', err);
        
        // Check for authentication errors
        if (err.response && err.response.status === 401) {
          setError('Authentication failed. Please log in again.');
          // Clear token and redirect to login
          localStorage.removeItem('token');
          setTimeout(() => navigate('/login'), 2000);
        } else if (err.response && err.response.status === 404) {
          setError(`Company with ID ${companyId} not found`);
        } else {
          setError('Failed to load company details. ' + (err.response?.data?.detail || err.message || 'Unknown error'));
        }
        
        setLoading(false);
      }
    };
    
    fetchCompany();
  }, [companyId, isAuthenticated, navigate]);
  
  // Fetch tax codes
  useEffect(() => {
    const fetchTaxCodes = async () => {
      try {
        if (!isAuthenticated) return;
        
        logger.log('Fetching tax codes');
        const data = await taxService.getTaxCodes();
        logger.log('Tax codes received:', data);
        
        if (Array.isArray(data)) {
          setTaxCodes(data);
        } else {
          logger.error('Received invalid tax codes data:', data);
          setError('Failed to load tax codes - invalid data format');
        }
      } catch (err) {
        logger.error('Error fetching tax codes:', err);
        setError('Failed to load tax codes: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
      }
    };
    
    fetchTaxCodes();
  }, [isAuthenticated]);
  
  // Update the selected tax code object when tax codes are loaded or selected tax code changes
  useEffect(() => {
    if (selectedTaxCode && taxCodes.length > 0) {
      const taxCodeObj = taxCodes.find(tc => tc.id === selectedTaxCode);
      if (taxCodeObj) {
        setSelectedTaxCodeObject(taxCodeObj);
      }
    } else {
      setSelectedTaxCodeObject(null);
    }
  }, [selectedTaxCode, taxCodes]);
  
  const handleTaxCodeChange = (event) => {
    setSelectedTaxCode(event.target.value);
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      logger.log(`Updating tax code for company ${companyId} to ${selectedTaxCode}`);
      await taxService.updateCompanyTaxCode(companyId, selectedTaxCode);
      
      logger.log('Tax code updated successfully');
      enqueueSnackbar('Tax code updated successfully', { variant: 'success' });
      setSaving(false);
      navigate(`/companies/${companyId}`);
    } catch (err) {
      logger.error('Error updating tax code:', err);
      setSaving(false);
      setError('Failed to update tax code: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
      enqueueSnackbar('Failed to update tax code', { variant: 'error' });
    }
  };
  
  const handleCancel = () => {
    navigate(`/companies/${companyId}`);
  };
  
  const handleSyncTaxCodes = async () => {
    try {
      setSyncingTaxCodes(true);
      setSyncResult(null);
      setError(null);
      
      logger.log('Syncing tax code catalogue');
      const result = await taxService.syncTaxCodes();
      logger.log('Sync result:', result);
      
      setSyncResult({
        success: true,
        message: result?.message || 'Tax code catalogue refreshed'
      });
      
      // Refresh tax codes list
      const data = await taxService.getTaxCodes();
      if (Array.isArray(data)) {
        setTaxCodes(data);
      }
      
      enqueueSnackbar(result?.in_progress ? 'Refreshing the tax code catalogue in the background' : 'Tax codes synced successfully',
        { variant: result?.in_progress ? 'info' : 'success' });
      setSyncingTaxCodes(false);
    } catch (err) {
      logger.error('Error syncing tax codes:', err);
      setSyncingTaxCodes(false);
      setSyncResult({
        success: false,
        message: err.response?.data?.detail || 'Failed to sync tax codes: ' + (err.message || 'Unknown error')
      });
      enqueueSnackbar('Failed to sync tax codes', { variant: 'error' });
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error && !company) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  
  return (
    <Card>
      <CardHeader 
        title={`Update Company Tax Code`} 
        subheader={company ? `${company.name} (ID: ${company.id})` : ''}
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="body1" sx={{ mb: 3 }}>
          Tax codes help GoTruTax determine the correct tax treatment for goods and services. The tax code you
          select will be used as the default for this company's transactions.
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3 }} color="text.secondary">
          <strong>Note:</strong> Start simple – default everything to a single, reasonably broad code (often txcd_99999999 "General - Tangible Goods", or txcd_00000000 "Nontaxable" for items that should never be taxed). 
          When you integrate with transactions, you will be able to specify product tax codes at the item level for more accurate tax calculations.
        </Typography>
        
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mr: 2 }}>
            Available Tax Codes
          </Typography>
          <IconButton 
            onClick={handleSyncTaxCodes} 
            disabled={syncingTaxCodes}
            title="Refresh the tax code catalogue"
            color="primary"
            size="small"
          >
            {syncingTaxCodes ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Box>
        
        {syncResult && (
          <Alert 
            severity={syncResult.success ? "success" : "error"} 
            sx={{ mb: 2 }}
          >
            {syncResult.message}
          </Alert>
        )}
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Autocomplete
            options={taxCodes}
            getOptionLabel={(option) => `${option.name} (${option.id})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedTaxCodeObject}
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedTaxCode(newValue.id);
                setSelectedTaxCodeObject(newValue);
              } else {
                setSelectedTaxCode('');
                setSelectedTaxCodeObject(null);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name} ({option.id})
              </li>
            )}
            loading={loading}
            loadingText="Loading tax codes..."
            noOptionsText="No tax codes found"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Tax Code"
                disabled={saving}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={saving}
          />
          {selectedTaxCode && (
            <FormHelperText>
              {taxCodes.find(tc => tc.id === selectedTaxCode)?.description || ''}
            </FormHelperText>
          )}
        </FormControl>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            onClick={handleCancel} 
            sx={{ mr: 2 }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Tax Code'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CompanyTaxCode; 